'use client';

/**
 * AI Assistant — page orchestrator.
 *
 * Thin wrapper that composes:
 * - ChatProvider (reused — all hooks, stores, context)
 * - AssistantLayout (v3 full-page chrome: h-dvh flex row, warm canvas)
 * - The [sidebar | main column] split (this file owns it — AssistantLayout
 *   stays a thin shell) + conditional content by UIState (empty / chat / call)
 *
 * v3 (design turn 6): there is no separate marketing landing anymore —
 * UIState.LANDING renders inside the SAME frame as CHAT/HANDOFF (sidebar +
 * header + composer always mounted), just with the unified empty-state
 * content (orb + headline + StarterGrid) in place of the transcript.
 *
 * Handles ?prompt= query param from AssistantPopover navigation.
 * Imports tool-registrations.ts as a side-effect to register tool
 * renderers in the global tool registry.
 *
 * Route: /us-en/ai-assistant/
 */

import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// ── Data layer (reused) ───────────────────────────────────────────
import { ChatProvider, useChatContext } from '@/contexts/ChatContext';
import { useAssistantConnection } from '@/machines/connection/useAssistantConnection';
import { useAssistantSession } from '@/hooks/useAssistantSession';
import { useOfflineAfterGrace } from '@/hooks/useOfflineAfterGrace';
import { useTimeoutRecovery } from '@/hooks/useTimeoutRecovery';
import { shouldShowTypingDots } from '@/lib/conversation-status';
import {
  UIState,
  ConnectionStatus,
  MessageRole,
  ComponentType,
  HandoffPhase,
  HandoffEndReason,
} from '@/types/chat';
import { getComposerPlaceholder } from '@/lib/composer-placeholder';
import { Toaster } from 'sonner';

// ── Side-effect: register V2 tool renderers ───────────────────────
import '@/lib/tool-registrations';

// ── V2 components (all new) ───────────────────────────────────────
import { AssistantLayout } from '@/components/assistant/assistant-layout';
import { ConversationHeader } from '@/components/assistant/conversation-header';
import { ResetNotice } from '@/components/assistant/reset-notice';
import { ConversationArea } from '@/components/assistant/conversation-area';
import type { ChatLayoutRef } from '@/types/chat';
import { MessageBlock } from '@/components/assistant/message-block';
import { PresenceRibbon } from '@/components/assistant/presence-ribbon';
import { ContactInfoCard } from '@/components/assistant/contact-info-card';
import { HandoffQueueCard } from '@/components/assistant/handoff-queue-card';
import { HandoffTimeoutPanel } from '@/components/assistant/handoff-timeout-panel';
import { ToolTimeoutCard } from '@/components/assistant/tool-timeout-card';
import { ConversationRatingCard } from '@/components/assistant/conversation-rating-card';
import {
  shouldAskForFeedback,
  FeedbackSource,
  FEEDBACK_IDLE_TIMEOUT_MS,
  isHandoffLive,
} from '@/lib/conversation-feedback-ask';
import {
  getConversationFeedback,
  type FeedbackResponse,
} from '@/lib/api/feedback';
import { buildMessageFeedbackMap } from '@/lib/feedback-restore';
import { resolveAgentJoinedRow } from '@/lib/agent-joined-claim';
import ThinkingBlock from '@/components/assistant/thinking-block';
import { RagAnswerBlock } from '@/components/assistant/content/rag-answer-block';
import { groupMessagesForRender } from '@/lib/thinking-groups';
import type { RAGReference } from '@/types/rag';
import { PromptInputBar } from '@/components/assistant/prompt-input-bar';
import { TypingIndicator } from '@/components/assistant/typing-indicator';
import { CallScreen } from '@/components/assistant/voice/call-screen';
import { AnimatePresence, motion } from 'framer-motion';

// ── v3 (T6/T8): desktop docked history sidebar + fresh-chat starter grid ──
import { HistorySidebar } from '@/components/assistant/history-sidebar';
import { StarterGrid } from '@/components/assistant/starter-grid';
import {
  OrbAvatar,
  ORB_VARIANT,
} from '@/components/assistant/primitives/orb-avatar';

// ── Shared topics ─────────────────────────────────────────────────
import { type AssistantTopic } from '@/lib/assistant-topics';

/** localStorage key for the v3 HistorySidebar's collapsed/expanded persistence. */
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sara.sidebar.collapsed';

// ── Connection status badge ───────────────────────────────────────

function ConnectionStatusBadge({ status }: { status: ConnectionStatus }) {
  // Grace-period debounce shared with the embedded panel — see
  // hooks/useOfflineAfterGrace.ts for why the raw status can't be used.
  const show = useOfflineAfterGrace(status);

  // The hook already folds in "not connected" — an extra local check here
  // would just be a second place for the two to disagree.
  if (!show) return null;

  const config = {
    [ConnectionStatus.CONNECTING]: {
      bg: 'rgba(251, 191, 36, 0.15)',
      border: 'rgba(251, 191, 36, 0.3)',
      dot: 'bg-amber-500 animate-pulse',
      text: 'rgb(251, 191, 36)',
      label: 'Connecting...',
    },
    [ConnectionStatus.RECONNECTING]: {
      // Distinct copy from CONNECTING — the user already had a session and
      // we're transparently swapping the upstream OpenAI connection.
      bg: 'rgba(251, 191, 36, 0.15)',
      border: 'rgba(251, 191, 36, 0.3)',
      dot: 'bg-amber-500 animate-pulse',
      text: 'rgb(251, 191, 36)',
      label: 'Reconnecting…',
    },
    [ConnectionStatus.DISCONNECTED]: {
      bg: 'rgba(239, 68, 68, 0.15)',
      border: 'rgba(239, 68, 68, 0.3)',
      dot: 'bg-red-500',
      text: 'rgb(239, 68, 68)',
      label: 'Disconnected',
    },
  } as const;

  const c = config[status];
  if (!c) return null;

  return (
    <div
      className="fixed left-4 top-4 z-50 flex items-center gap-2 rounded-full px-[12px] py-1.5 shadow-lg backdrop-blur-sm lg:left-auto lg:right-4"
      style={{ backgroundColor: c.bg, border: `1px solid ${c.border}` }}
    >
      <div className={`size-2 rounded-full ${c.dot}`} />
      <span className="text-xs font-medium" style={{ color: c.text }}>
        {c.label}
      </span>
    </div>
  );
}

// ── Inner content (consumes ChatContext) ───────────────────────────

function AssistantV2Content() {
  const {
    messages,
    uiState,
    inputValue,
    setInputValue,
    connectionStatus,
    turnState,
    isRecording,
    handleVoiceRecording,
    // [FB34] callPhase (not isCalling) gates every external/user-facing call
    // surface concern below — see ChatContext.tsx's doc comment on the field.
    callPhase,
    toggleCall,
    chatLayoutRef,
    promptInputRef,
    sendText,
    retryMessage,
    isThinkingRef,
    // Landing headline lives in ChatContext (not page-local) so
    // startNewChat() can reset it to the default.
    headline,
    setHeadline,
    // Live-agent handoff — the pre-handoff contact-form card (Decision #17,
    // 2026-07-hubspot-native-handoff.md) renders below when
    // handoff?.phase === COLLECTING. submitContactInfo/sendEndHandoff are its
    // Continue/Cancel handlers.
    handoff,
    submitContactInfo,
    sendEndHandoff,
    // Session-recovery: the tool-timeout card (below) renders when this is
    // set — the ONE shared flow instance also driving the call surface's
    // CallToolTimeoutPanel (see useToolTimeoutFlow's header comment for why
    // this must be a single shared instance, not one computed per mount).
    toolTimeoutFlow,
    // v3 (T6): the HistorySidebar's "New" capsule reuses the same single
    // reset point as the popover/drawer's New button.
    startNewChat,
  } = useChatContext();

  // Connection machine driver (side-effect only hook) — owns the WebSocket,
  // the outbox, and event dispatch into ChatContext.
  useAssistantConnection();

  // ── v3 turn 5: graceful-end recap detection ──
  //
  // HandoffRecapCard (message-block.tsx) should take over the transcript row
  // that already shows the backend's end-of-handoff notice ("Your chat with
  // {name} has ended."/timeout copy) instead of duplicating it. That notice
  // arrives as a plain SYSTEM-role Message via the generic SYSTEM_MESSAGE WS
  // event; the handoff.phase → ENDED transition (HANDOFF_ENDED/TIMEOUT) is a
  // SEPARATE WS frame with no shared id linking the two (unlike
  // CONTACT_SUMMARY, which carries a dedicated `contactSummary` field on its
  // own Message — see types/chat.ts). Both events are owned by
  // useAssistantConnection.ts/ChatContext.tsx, which this task isn't allowed
  // to touch (a concurrent change owns those files), so this approximates
  // the link from the read-only `handoff` + `messages` this component already
  // has: the first time a handoff's phase reaches ENDED, if the LAST message
  // in the transcript at that instant is a SYSTEM row, claim it. In practice
  // the backend's end-of-handoff SYSTEM_MESSAGE lands before the
  // phase-changing event, so this is that row. One claim per handoffId (a
  // second handoff later in the same session gets its own recap). Known
  // limitation: a restored/reloaded conversation has no live `handoff` object
  // to trigger this, so a historical "has ended" row keeps rendering as the
  // plain system notice rather than the recap card — consistent with the
  // rest of the app's restored-rows-render-statically precedent.
  const claimedHandoffRecapsRef = useRef<Set<string>>(new Set());
  const [handoffRecapsByMessageId, setHandoffRecapsByMessageId] = useState<
    Record<string, { agentDisplayName?: string; endReason?: HandoffEndReason }>
  >({});

  useEffect(() => {
    if (!handoff || handoff.phase !== HandoffPhase.ENDED) return;
    if (claimedHandoffRecapsRef.current.has(handoff.handoffId)) return;
    claimedHandoffRecapsRef.current.add(handoff.handoffId);
    const last = messages[messages.length - 1];
    if (last?.role === MessageRole.SYSTEM) {
      // endReason is threaded through so HandoffRecapCard can suppress
      // itself for TIMEOUT_NO_AGENT — HandoffTimeoutPanel (rendered below,
      // via isHandoffTimedOut) owns that moment instead. See that
      // component's header comment.
      setHandoffRecapsByMessageId((prev) => ({
        ...prev,
        [last.id]: {
          agentDisplayName: handoff.agentDisplayName,
          endReason: handoff.endReason,
        },
      }));
    }
  }, [handoff, messages]);

  // ── Center status chip: "{name} joined the chat" ──
  //
  // Same claim-by-transition INTENT as the ENDED recap above, keyed on the
  // WAITING/COLLECTING → CONNECTED transition instead — but NOT the same
  // "whatever's last" mechanism (Defect 5): AGENT_JOINED and the backend's
  // SYSTEM_MESSAGE carrying "{name} joined the chat" are two independent WS
  // sends with no ordering guarantee between them, unlike HANDOFF_ENDED's
  // SYSTEM_MESSAGE, which the backend sends synchronously in the same code
  // path just before that phase event. If the transition lands first,
  // "whatever's last right now" could be some earlier, unrelated system row
  // (e.g. the WAITING queue notice) that just happens to still be last —
  // permanently hijacking it and destroying its real text.
  //
  // Fix: track `preConnectMessagesLenRef`, the transcript length as of the
  // last render where this handoff was NOT yet connected, and only ever
  // claim a SYSTEM row at or after that index (resolveAgentJoinedRow — see
  // its doc comment). A pre-existing row can never be at or past that
  // index, so it can never be hijacked; if the real "joined" row hasn't
  // landed yet, this just re-runs (via the `messages` dependency) once it
  // does — no claim happens until then. One claim per handoffId — a handoff
  // only ever transitions into CONNECTED once.
  const claimedAgentJoinedRef = useRef<Set<string>>(new Set());
  const preConnectMessagesLenRef = useRef<Record<string, number>>({});
  const [agentJoinedChipsByMessageId, setAgentJoinedChipsByMessageId] =
    useState<Record<string, { agentDisplayName?: string }>>({});

  useEffect(() => {
    if (!handoff) return;
    if (handoff.phase !== HandoffPhase.CONNECTED) {
      preConnectMessagesLenRef.current[handoff.handoffId] = messages.length;
      return;
    }
    if (claimedAgentJoinedRef.current.has(handoff.handoffId)) return;
    const sinceIndex =
      preConnectMessagesLenRef.current[handoff.handoffId] ?? messages.length;
    const row = resolveAgentJoinedRow(messages, sinceIndex);
    if (!row) return; // Not landed yet.
    claimedAgentJoinedRef.current.add(handoff.handoffId);
    setAgentJoinedChipsByMessageId((prev) => ({
      ...prev,
      [row.id]: { agentDisplayName: handoff.agentDisplayName },
    }));
  }, [handoff, messages]);

  // ── Presence ribbon: close the TIMED_OUT "recovery window" once the user
  // resumes ordinary chatting (Defect 7) ──
  //
  // See presence-ribbon-text.ts's hasResumedChatAfterTimeout for the "why
  // USER-authored, why a baseline" reasoning — this effect just supplies
  // that baseline the same way the claim effect above does: the transcript
  // length as of the last render where this handoff was NOT yet TIMED_OUT.
  // One-shot per handoffId once resumed — a handoff never leaves TIMED_OUT
  // within a session, so there's nothing to re-arm.
  const {
    showTimeoutPanel,
    timeoutPanelHandoffId,
    contactSummaryForHandoff,
    timeoutRecoveryWindowClosed,
  } = useTimeoutRecovery();

  // Composer placeholder — phase-aware copy (see lib/composer-placeholder.ts
  // for why this keys on `handoff?.phase` rather than `uiState`).
  const composerPlaceholder = getComposerPlaceholder(
    handoff?.phase,
    handoff?.agentDisplayName
  );

  // ── v3 (T6): desktop HistorySidebar collapsed/expanded, persisted ──
  // Lazy-init reads localStorage directly, so it must guard `typeof window`
  // for SSR (this component's initial render also happens server-side).
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
  });
  const handleToggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      }
      return next;
    });
  }, []);

  // ── Entry intent, conversation identity, and ?prompt= release ──
  //
  // Extracted to a shared hook so the embedded panel (/us-en/ai-assistant/embed)
  // gets identical entry semantics rather than a second implementation. The
  // rationale — and the production bugs the logic defends against — live in
  // src/hooks/useAssistantSession.ts.
  const { expectingHistory, resumeBooting, hasQueuedPrompt, conversationId } =
    useAssistantSession();

  // ── Feedback UI (turn 19): restore + the four rating-card triggers ──
  //
  // Restore: fetch every feedback row recorded so far for this conversation
  // once it's known, so a message the user already rated shows its thumb
  // again after a reload instead of a blank row (lib/feedback-restore.ts
  // does the array -> per-message map reduction; kept pure/tested there
  // rather than inlined here). Best-effort — a failed fetch just leaves
  // every row unrated, same "never block the conversation" posture as
  // everything else in this feature; nothing surfaces the error to the user.
  const [feedbackByMessageId, setFeedbackByMessageId] = useState<
    Record<string, FeedbackResponse>
  >({});
  useEffect(() => {
    if (!conversationId) {
      setFeedbackByMessageId({});
      return;
    }
    let cancelled = false;
    getConversationFeedback(conversationId)
      .then((responses) => {
        if (!cancelled)
          setFeedbackByMessageId(buildMessageFeedbackMap(responses));
      })
      .catch(() => {
        /* best-effort restore; see this block's header comment */
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Which trigger (if any) is currently asking for a whole-conversation
  // rating. All four triggers below share this one piece of state and the
  // one `askedFeedbackRef` "have we asked this conversation yet" flag —
  // shouldAskForFeedback (lib/conversation-feedback-ask.ts) is the single
  // pure decision every trigger consults before setting it, per that
  // module's header comment ("don't scatter the decision across effects").
  const [feedbackAsk, setFeedbackAsk] = useState<{
    source: FeedbackSource;
    handoffId?: string;
  } | null>(null);
  const askedFeedbackRef = useRef(false);
  const claimedHandoffFeedbackRef = useRef<Set<string>>(new Set());

  // A fresh/resumed conversation gets its own "have we asked" slate.
  useEffect(() => {
    askedFeedbackRef.current = false;
    setFeedbackAsk(null);
    claimedHandoffFeedbackRef.current = new Set();
  }, [conversationId]);

  // Trigger 1: handoff_wrap — a handoff that just ended NORMALLY (not
  // timeout_no_agent, which HandoffTimeoutPanel already owns — see that
  // component and handoff-recap-card.tsx's identical endReason guard).
  // Accompanies HandoffRecapCard rather than replacing it: the recap stays
  // message-block.tsx's job of restyling the backend's own "chat ended"
  // system row; this renders as its own additional card at the transcript
  // tail, same "lives in the lane" precedent as the cards below it.
  useEffect(() => {
    if (!handoff || handoff.phase !== HandoffPhase.ENDED) return;
    if (handoff.endReason === HandoffEndReason.TIMEOUT_NO_AGENT) return;
    if (claimedHandoffFeedbackRef.current.has(handoff.handoffId)) return;
    claimedHandoffFeedbackRef.current.add(handoff.handoffId);
    if (
      shouldAskForFeedback({
        source: FeedbackSource.HANDOFF_WRAP,
        alreadyAskedThisConversation: askedFeedbackRef.current,
        handoffLive: false,
        hasDraftText: false,
      })
    ) {
      askedFeedbackRef.current = true;
      setFeedbackAsk({
        source: FeedbackSource.HANDOFF_WRAP,
        handoffId: handoff.handoffId,
      });
    }
  }, [handoff]);

  // Trigger 2 (of 4): idle — 3 minutes (FEEDBACK_IDLE_TIMEOUT_MS) of
  // inactivity, restarted on any new message or composer keystroke.
  // shouldAskForFeedback enforces "never while a handoff is live or the
  // composer has draft text"; the once-per-conversation cap is the same
  // askedFeedbackRef every other non-menu trigger shares. Skipped entirely
  // before the first message — an untouched landing screen has nothing to
  // rate yet.
  //
  // `handoffLive` goes through isHandoffLive (an allow-list of
  // COLLECTING/WAITING/CONNECTED) rather than a raw `phase !== ENDED` check
  // — TIMED_OUT is ALSO terminal (no rep coming) but, unlike ENDED, nothing
  // ever transitions a handoff out of it within a session, so a deny-list
  // keyed only on ENDED would read a timed-out handoff as "live" forever and
  // permanently suppress this trigger for the rest of the conversation. See
  // isHandoffLive's doc comment (conversation-feedback-ask.ts).
  useEffect(() => {
    if (feedbackAsk || messages.length === 0) return;
    const timer = window.setTimeout(() => {
      if (
        shouldAskForFeedback({
          source: FeedbackSource.IDLE,
          alreadyAskedThisConversation: askedFeedbackRef.current,
          handoffLive: isHandoffLive(handoff?.phase),
          hasDraftText: inputValue.trim().length > 0,
        })
      ) {
        askedFeedbackRef.current = true;
        setFeedbackAsk({ source: FeedbackSource.IDLE });
      }
    }, FEEDBACK_IDLE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [messages, inputValue, handoff, feedbackAsk]);

  // Trigger 3: menu — "Give feedback" in the header's "⋯" menu (Part 3).
  // Deliberately does NOT set askedFeedbackRef: a manual, explicit ask from
  // the menu is exempt from (and must not consume) the once-per-conversation
  // cap the other three triggers share — see shouldAskForFeedback's MENU
  // branch.
  const handleGiveFeedback = useCallback(() => {
    setFeedbackAsk({ source: FeedbackSource.MENU });
  }, []);

  // Trigger 4 (end_chat) is intentionally NOT wired. This app has no
  // deliberate "I'm done" action to hang it on, and the header's X is not a
  // substitute: intercepting the exit someone just pressed in order to ask
  // them for a rating is a dark pattern, and it contradicts this feature's
  // own principle that nothing blocks the conversation. FeedbackSource
  // keeps END_CHAT so the contract is ready if a real end-chat action ever
  // ships; until then the ask arrives via handoff_wrap, idle, or the menu.

  // ── Handle suggestion card click ──
  const handleTopicSelect = useCallback(
    (topic: AssistantTopic) => {
      setHeadline(topic.headline);
      sendText(topic.prompt);
    },
    [sendText, setHeadline]
  );

  // ── Determine if assistant is processing ──
  // The "Sara is working" affordance — the typing dots. Primary signal: the
  // backend's turn_state phase (authoritative — spans the commentary→tool→
  // generation sequence that message-shape inference can't see). Fallback when
  // turnState is null (old backend / turn over): the legacy
  // unanswered-live-user-message derivation. See shouldShowTypingDots for the
  // full rule. (The old status LINE above the composer — "Responding..",
  // per-tool copy — was a v2-design relic, retired 2026-07-13; the Thinking
  // timeline's "Working…" header carries live activity now.)
  //
  // Suppressed entirely during a live-agent handoff: the legacy fallback would
  // otherwise read a fresh USER message (a reply typed to the HUMAN AGENT) as
  // "awaiting Sara's reply" and show her typing dots — wrong, since Sara isn't
  // generating anything during a handoff (turnState is already cleared to null
  // on HANDOFF_STARTED) and HubSpot exposes no typing signal to show instead
  // (Decision #16).
  const showTyping =
    uiState === UIState.HANDOFF
      ? false
      : shouldShowTypingDots(turnState, messages);

  // Group each assistant segment into one turn so the Thinking timeline + the
  // composed RAG answer (hero→answer→sources) render as a unit. Only the last
  // turn is "current": it shows the live timeline and withholds its sources
  // until the turn is done (backend turnState back to null).
  const renderItems = useMemo(
    () => groupMessagesForRender(messages),
    [messages]
  );

  // v3 (T6): the empty state (orb + headline + StarterGrid) now covers BOTH
  // a brand-new visit (UIState.LANDING) and a fresh chat started via "New"
  // with nothing sent yet (UIState.CHAT, zero messages) — there's no more
  // separate marketing landing screen, just this one empty-state content
  // rendered inside the same frame. Excludes HANDOFF (can't be active with
  // zero messages in practice) and the expectingHistory skeleton case below.
  //
  // Also excluded: a still-queued ?prompt= (the URL keeps it until the
  // release strips it in the sync effect above). The prompt WILL become the
  // first message within a few frames — painting the starter grid first and
  // yanking it away read as a glitch (owner report, 2026-07-13); a briefly
  // blank surface is calmer.
  const showEmptyState =
    messages.length === 0 &&
    !expectingHistory &&
    !resumeBooting &&
    !hasQueuedPrompt &&
    (uiState === UIState.LANDING || uiState === UIState.CHAT);

  // v3 (T6): sidebar + header + composer now render for EVERY uiState — the
  // v3 frame IS the page, there's no more landing-vs-expanded split. Only the
  // transcript region (presence ribbon + conversation surface) still gates
  // on non-CALL: CallScreen's full-screen orb overlay (mounted at the bottom
  // of the main column below) covers that region entirely during a call, so
  // this preserves the pre-existing "CALL renders neither" gap noted below.
  const showTranscriptSurface = uiState !== UIState.CALL;

  // Input bar — built once, mounted once (the v3 frame has a single main
  // column now, not a separate LANDING slot vs. a CHAT/HANDOFF column) via
  // the exact original condition (`uiState !== UIState.CALL`, i.e. shown on
  // LANDING too).
  //
  // `callPhase !== 'idle'` (v3, migrated for FB34): the call overlay
  // (CallScreen) now owns every voice control (mute/end/CC) and mounts the
  // INSTANT a call is tapped — gating on the old `isCalling` here left the
  // composer focusable (and Enter-submittable) underneath the overlay for
  // the whole `connecting` phase, since isCalling doesn't flip until well
  // after the surface is already up.
  const inputBar =
    uiState !== UIState.CALL && callPhase === 'idle' ? (
      <div className="shrink-0">
        <PromptInputBar
          ref={promptInputRef}
          value={inputValue}
          onChange={setInputValue}
          onSubmit={sendText}
          onVoice={handleVoiceRecording}
          onCallToggle={toggleCall}
          isRecording={isRecording}
          hideVoiceControls={uiState === UIState.HANDOFF}
          placeholder={composerPlaceholder}
        />
      </div>
    ) : null;

  return (
    <>
      <ConnectionStatusBadge status={connectionStatus} />

      <AssistantLayout>
        {/* ── v3 (T6/T8): desktop history sidebar ── renders in every
            uiState now (previously gated to CHAT/HANDOFF alongside the
            conversation column). Hidden below `lg:` — mobile uses the
            full-screen ConversationHistoryDrawer instead, opened from
            ConversationHeader. */}
        <div className="hidden shrink-0 lg:block">
          <HistorySidebar
            collapsed={sidebarCollapsed}
            onToggleCollapsed={handleToggleSidebarCollapsed}
            onNewChat={startNewChat}
          />
        </div>

        {/* Main column — header + content + composer. `relative` so
            CallScreen (absolute inset-0, mounted at the bottom) overlays
            only this column, never the sidebar. `min-w-0` is load-bearing:
            without it, any wide chat content (course rows, long unbroken
            strings) sets the column's min-content width past the phone
            viewport, dragging the header, composer AND the call overlay's
            containing block off-screen with it (FB26). */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-[#FFFAF5]">
          <ConversationHeader
            restorePending={resumeBooting || expectingHistory}
            onNewChat={startNewChat}
            onGiveFeedback={handleGiveFeedback}
          />

          {/* Foreign-?c= reset notice (declarative; survives the reset navigation) */}
          <ResetNotice />

          {showTranscriptSurface && (
            <>
              {/* v3 turn 5: warm persistent presence ribbon — pinned above
                  ConversationArea (not the input bar) so it stays visible
                  while the transcript scrolls beneath it. Self-guards on
                  `handoff` being null/ENDED (see presence-ribbon.tsx), so
                  it's a no-op outside an active handoff.
                  `timeoutRecoveryWindowClosed` (Defect 7) hides the
                  TIMED_OUT variant once resumedChatAfterTimeout above has
                  latched for the current handoff — otherwise it would stay
                  pinned for the rest of the session, since nothing else
                  ever transitions a handoff out of TIMED_OUT. */}
              <PresenceRibbon
                timeoutRecoveryWindowClosed={timeoutRecoveryWindowClosed}
              />
              <ConversationArea
                ref={chatLayoutRef as React.RefObject<ChatLayoutRef>}
              >
                {/* mode="wait" cross-fades the surface swaps below (welcome →
                    conversation on first send, and back on "New"): the
                    outgoing branch fades out fully before the incoming one
                    fades in, so both are never in the flow at once — an
                    opacity-only transition with zero layout shift (the
                    instant swap was one of the first-message flicker's
                    mechanisms). initial={false} keeps the very first paint
                    animation-free. The input bar lives outside this surface,
                    so interactivity is never delayed. Every exit carries
                    pointerEvents:'none' (framer applies non-animatable
                    values immediately at exit start): under mode="wait" the
                    outgoing branch stays in the DOM for the full fade, so a
                    still-clickable exiting StarterGrid could double-send on
                    a fast double-tap. */}
                <AnimatePresence mode="wait" initial={false}>
                  {messages.length === 0 &&
                  (expectingHistory || resumeBooting) ? (
                    // Restore skeleton — shown ONLY while a resume awaits its
                    // HISTORY_RESTORED replay (the store's expectingHistory intent
                    // flag). A brand-new chat never renders this: it has nothing to
                    // load, and pulsing bubbles on an idle screen read as a hang
                    // (#79). Cleared on restore success or HISTORY_REPLAY_FAILED.
                    <motion.div
                      key="restore-skeleton"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, pointerEvents: 'none' }}
                      transition={{ duration: 0.15 }}
                      className="flex flex-col gap-[16px] py-8"
                    >
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex flex-col gap-[12px]">
                          <div
                            className={`h-10 animate-pulse rounded-[20px] bg-[#f2f0ee] ${
                              i % 2 === 0 ? 'ml-auto w-[40%]' : 'w-[70%]'
                            }`}
                          />
                        </div>
                      ))}
                    </motion.div>
                  ) : showEmptyState ? (
                    // v3 (T6/T8): unified empty state — a brand-new visit
                    // (LANDING) and a fresh chat with nothing sent yet (CHAT,
                    // "New" was clicked) now render identically: orb + dynamic
                    // headline + starter prompts, centered in the same
                    // conversation surface (no separate landing screen).
                    <motion.div
                      key="empty-state"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, pointerEvents: 'none' }}
                      transition={{ duration: 0.15 }}
                      className="m-auto flex w-full flex-col items-center gap-6 px-[16px] py-6"
                    >
                      <div
                        className="flex items-center justify-center rounded-full"
                        style={{ boxShadow: '0 0 0 10px rgba(255,183,74,.14)' }}
                      >
                        <OrbAvatar size={64} variant={ORB_VARIANT.CHAT} />
                      </div>
                      <h1 className="max-w-md text-center font-[family-name:var(--font-lora)] text-2xl font-semibold text-[#31364e]">
                        {headline}
                      </h1>
                      <StarterGrid onSelect={handleTopicSelect} />
                    </motion.div>
                  ) : (
                    // Stable key: this wrapper mounts once per conversation
                    // surface — individual message appends never re-trigger the
                    // fade. It reproduces the parent Content's flex column +
                    // gap-[20px] so message spacing is unchanged.
                    <motion.div
                      key="conversation"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, pointerEvents: 'none' }}
                      transition={{ duration: 0.15 }}
                      className="flex w-full flex-col gap-[20px]"
                    >
                      {renderItems.map((item, itemIdx) => {
                        if (item.kind === 'message') {
                          const msg = item.message;
                          return (
                            <MessageBlock
                              key={msg.id}
                              messageId={msg.id}
                              dbId={msg.dbId}
                              userPrompt={
                                msg.role === MessageRole.USER
                                  ? msg.content
                                  : undefined
                              }
                              userStatus={
                                msg.role === MessageRole.USER
                                  ? msg.status
                                  : undefined
                              }
                              onRetry={
                                msg.role === MessageRole.USER
                                  ? () => retryMessage(msg.id)
                                  : undefined
                              }
                              agentContent={
                                msg.role === MessageRole.ASSISTANT
                                  ? msg.content
                                  : undefined
                              }
                              systemContent={
                                msg.role === MessageRole.SYSTEM
                                  ? msg.content
                                  : undefined
                              }
                              contactSummary={
                                msg.role === MessageRole.SYSTEM
                                  ? msg.contactSummary
                                  : undefined
                              }
                              supportRequestSubmitted={
                                msg.role === MessageRole.SYSTEM
                                  ? msg.supportRequestSubmitted
                                  : undefined
                              }
                              handoffEndedRecap={
                                msg.role === MessageRole.SYSTEM
                                  ? handoffRecapsByMessageId[msg.id]
                                  : undefined
                              }
                              agentJoinedChip={
                                msg.role === MessageRole.SYSTEM
                                  ? agentJoinedChipsByMessageId[msg.id]
                                  : undefined
                              }
                              toolOutcome={
                                msg.role === MessageRole.SYSTEM
                                  ? msg.toolOutcome
                                  : undefined
                              }
                              restored={msg.restored}
                              humanAgentContent={
                                msg.role === MessageRole.HUMAN_AGENT
                                  ? msg.content
                                  : undefined
                              }
                              humanAgentRichText={
                                msg.role === MessageRole.HUMAN_AGENT
                                  ? msg.richText
                                  : undefined
                              }
                              agentDisplayName={
                                msg.role === MessageRole.HUMAN_AGENT
                                  ? msg.agentDisplayName
                                  : undefined
                              }
                              attachments={
                                msg.role === MessageRole.HUMAN_AGENT
                                  ? msg.attachments
                                  : undefined
                              }
                              courseList={
                                msg.result?.kind ===
                                ComponentType.COURSE_BROWSER
                                  ? msg.result.courses
                                  : undefined
                              }
                              courseSearchedLocation={
                                msg.result?.kind ===
                                ComponentType.COURSE_BROWSER
                                  ? msg.result.searchedLocation
                                  : undefined
                              }
                              ragContent={
                                msg.result?.kind === ComponentType.RAG_CONTENT
                                  ? msg.result.ragContent
                                  : undefined
                              }
                              streaming={msg.streaming ?? false}
                              conversationId={conversationId}
                              // Keyed by dbId, NOT msg.id. This map is built
                              // from the DB-scoped GET /feedback response, whose
                              // keys are messages.id — the same two-id split the
                              // reaction row submits under (see Message.dbId).
                              // Indexing it with the client-visible id happened to
                              // work only because the fetch fires at restore time,
                              // when the two coincide; it would silently miss on
                              // any live message the moment that stopped holding.
                              existingFeedback={
                                msg.dbId
                                  ? feedbackByMessageId[msg.dbId]
                                  : undefined
                              }
                            />
                          );
                        }

                        // assistant-turn — Thinking timeline + composed / inline body.
                        // "Current" = this turn is the LAST item in the whole
                        // conversation — not merely the last assistant turn. The
                        // distinction is what keeps settled turns settled: after
                        // the user sends the next message (and until Sara's new
                        // turn produces its first output), the PREVIOUS turn is
                        // still the last *assistant* turn, and the old
                        // `lastTurnId` rule flipped it back to
                        // in-progress the moment turnState went live — its
                        // sources panel unmounted (visible flash) and its
                        // thinking chip went back to a live label. A turn with
                        // anything after it is settled by definition and must
                        // never revert. Only the current turn shows the live
                        // timeline and withholds sources until the backend turn
                        // is done.
                        //
                        // The streaming OR-clause covers the one case where a
                        // later item can land while this turn is still producing
                        // output (e.g. the user sends a follow-up mid-stream):
                        // last-position alone would settle the streaming turn —
                        // freezing its live timeline and releasing sources early.
                        // A turn with any message still streaming is current by
                        // definition. (system_message items can't create this
                        // race: the backend only emits them after terminalizing
                        // the turn's state.)
                        const hasLiveStream =
                          item.commentary.some((m) => m.streaming) ||
                          item.cards.some((m) => m.streaming) ||
                          item.answer?.streaming === true;
                        const isCurrent =
                          itemIdx === renderItems.length - 1 || hasLiveStream;
                        const responding = turnState !== null && isCurrent;
                        const answerDone = !responding;

                        const commentaryText = item.commentary
                          .map((m) => m.content)
                          .filter(
                            (c): c is string =>
                              typeof c === 'string' && c.trim().length > 0
                          );
                        const turnRefs: RAGReference[] = item.cards.flatMap(
                          (m) =>
                            m.result?.kind === ComponentType.RAG_CONTENT
                              ? (m.result.ragContent.references?.references ??
                                [])
                              : []
                        );
                        const answerText =
                          typeof item.answer?.content === 'string'
                            ? item.answer.content
                            : null;
                        const answerStarted =
                          answerText !== null && answerText.trim().length > 0;

                        const thinking =
                          commentaryText.length > 0 || turnRefs.length > 0 ? (
                            <ThinkingBlock
                              inProgress={responding}
                              answerStarted={answerStarted}
                              commentary={commentaryText}
                              refs={turnRefs}
                              workedSeconds={item.workedSeconds}
                              turnStartedAt={item.turnStartedAt}
                              liveTool={
                                responding ? (turnState?.tool ?? null) : null
                              }
                            />
                          ) : null;

                        // RAG turn → composed hero→answer→sources (card B, the default).
                        if (turnRefs.length > 0) {
                          const courseCards = item.cards.filter(
                            (m) =>
                              m.result?.kind === ComponentType.COURSE_BROWSER
                          );
                          return (
                            <div key={item.id} className="flex flex-col gap-2">
                              {thinking}
                              {courseCards.map((m) => (
                                <MessageBlock
                                  key={m.id}
                                  courseList={
                                    m.result?.kind ===
                                    ComponentType.COURSE_BROWSER
                                      ? m.result.courses
                                      : undefined
                                  }
                                  courseSearchedLocation={
                                    m.result?.kind ===
                                    ComponentType.COURSE_BROWSER
                                      ? m.result.searchedLocation
                                      : undefined
                                  }
                                  streaming={false}
                                  restored={m.restored}
                                />
                              ))}
                              <RagAnswerBlock
                                refs={turnRefs}
                                answer={answerText}
                                answerDone={answerDone}
                                // Restored turns render statically (the
                                // reloaded-transcript invariant); the media
                                // rows carry the restored flag.
                                animateMediaIn={
                                  !item.cards.some((m) => m.restored)
                                }
                              />
                            </div>
                          );
                        }

                        // Non-RAG turn → cards + answer as their own inline blocks.
                        return (
                          <div key={item.id} className="flex flex-col gap-2">
                            {thinking}
                            {item.cards.map((m) => (
                              <MessageBlock
                                key={m.id}
                                courseList={
                                  m.result?.kind ===
                                  ComponentType.COURSE_BROWSER
                                    ? m.result.courses
                                    : undefined
                                }
                                courseSearchedLocation={
                                  m.result?.kind ===
                                  ComponentType.COURSE_BROWSER
                                    ? m.result.searchedLocation
                                    : undefined
                                }
                                ragContent={
                                  m.result?.kind === ComponentType.RAG_CONTENT
                                    ? m.result.ragContent
                                    : undefined
                                }
                                streaming={false}
                                restored={m.restored}
                              />
                            ))}
                            {item.answer && (
                              <MessageBlock
                                key={item.answer.id}
                                // Not passed elsewhere in this grouped-turn
                                // branch (course/RAG cards above have no
                                // reaction row) — needed here so
                                // MessageReactionRow has a stable id to scope
                                // its feedback to.
                                messageId={item.answer.id}
                                dbId={item.answer.dbId}
                                agentContent={item.answer.content}
                                streaming={item.answer.streaming ?? false}
                                conversationId={conversationId}
                                existingFeedback={
                                  item.answer.dbId
                                    ? feedbackByMessageId[item.answer.dbId]
                                    : undefined
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                      {/* Typing indicator lives INSIDE the conversation
                          surface (owner decision 2026-07-21): it may only
                          ever appear below the message thread. When it sat
                          outside the AnimatePresence block, the mode="wait"
                          swap left it rendering mid-screen beneath the
                          fading empty state for ~150ms before jumping to
                          the top. Hidden during a call (CallScreen overlay
                          is the indicator), including while `connecting`
                          (FB34: gated on callPhase, not isCalling). */}
                      <TypingIndicator
                        visible={showTyping && callPhase === 'idle'}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Pre-handoff contact-form card (Decision #17,
                2026-07-hubspot-native-handoff.md) — inline at the bottom of
                the message list, INSIDE the scrollable conversation area so
                it reads as part of the conversation, not a modal. uiState
                stays CHAT for this phase (see the CONTACT_INFO_REQUESTED
                case in useAssistantConnection.ts), so the input bar below
                keeps rendering and routing to Sara while this is up. */}
                {/* AnimatePresence lets the card's collapse-exit play when the
                phase leaves COLLECTING (submit or cancel) — paired with
                ContactSummaryCard's delayed expand-in, the submit reads as
                the form folding into the receipt. */}
                <AnimatePresence initial={false}>
                  {handoff?.phase === HandoffPhase.COLLECTING && (
                    <ContactInfoCard
                      key="contact-info-card"
                      fields={handoff.fields ?? []}
                      onSubmit={submitContactInfo}
                      onCancel={sendEndHandoff}
                    />
                  )}
                </AnimatePresence>
                {/* Queue card (Decision: timeout-recovery flow) — inline at
                the tail of the transcript while phase === WAITING, the same
                "lives in the conversation lane" precedent as ContactInfoCard
                above. No enter/exit motion — the queue card's own progress
                sweep is the only animation this surface needs; mirrors
                PresenceRibbon's plain-conditional-render precedent. */}
                {handoff?.phase === HandoffPhase.WAITING && (
                  <HandoffQueueCard />
                )}
                {/* Timeout recovery panel — ONE mount path for both a LIVE
                timeout (phase === TIMED_OUT, kept mounted through a
                subsequent HANDOFF_ENDED(timeout_no_agent) — see
                isHandoffTimedOut above and handoff-recap-card.tsx's
                endReason guard) AND a RESTORED, still-outstanding offer
                found after a page reload (no live `handoff` at all — see
                lib/handoff-timeout-restore.ts). A second mount path for the
                same stateful form would be a divergence bug waiting to
                happen, so both cases resolve through the same
                `timeoutPanelHandoffId` (used for both `key` — resetting the
                panel's internal view machine per handoff — and the
                `handoffId` prop) and the same `showTimeoutPanel` gate.

                When the user submits through a RESTORED panel, no new
                support_request_submitted row enters `messages` this
                session — the POST persists server-side only, and the panel
                shows its own SUBMITTED view for the rest of the session via
                its internal view machine. The marker row (and this panel's
                supersession by it) only appears on the NEXT reload. This is
                intentional — no live message injection here. */}
                {showTimeoutPanel && timeoutPanelHandoffId && (
                  <HandoffTimeoutPanel
                    key={`handoff-timeout-panel-${timeoutPanelHandoffId}`}
                    handoffId={timeoutPanelHandoffId}
                    conversationId={conversationId}
                    email={contactSummaryForHandoff?.email}
                    phoneOnFile={contactSummaryForHandoff?.phone ?? undefined}
                  />
                )}
                {/* Session-recovery: tool-timeout card. `toolTimeoutFlow`
                (ChatContext) is the ONE shared instance driving BOTH this
                card and the call surface's CallToolTimeoutPanel — see
                useToolTimeoutFlow's own header comment for why a
                per-mount instance here was a real divergence bug (the
                transcript stays mounted underneath the call overlay for
                the whole call, so two independent instances could disagree
                on the same incident's outcome). No `key` needed anymore:
                the hook itself now resets on a toolName change (also in
                that header comment), so this component doesn't need a
                fresh React mount to stay correct across incidents. */}
                {toolTimeoutFlow && <ToolTimeoutCard flow={toolTimeoutFlow} />}
                {/* Conversation rating card (feedback UI, turn 19b) — inline
                at the transcript tail, same "lives in the lane, never a
                modal" precedent as the cards above. Three triggers
                (handoff_wrap/idle/menu) set feedbackAsk; see that state's
                declaration for the shared once-per-conversation gating that
                all but menu respect. */}
                {feedbackAsk && conversationId && (
                  <ConversationRatingCard
                    key={`feedback-${feedbackAsk.source}-${feedbackAsk.handoffId ?? conversationId}`}
                    conversationId={conversationId}
                    source={feedbackAsk.source}
                    handoffId={feedbackAsk.handoffId}
                  />
                )}
              </ConversationArea>
            </>
          )}

          {inputBar}

          {/* v3 voice call overlay — absolute inset-0 within this main
              column's `relative` root (never the sidebar — see the wrapper
              above), so it covers the header + content + composer,
              regardless of where in this tree it's mounted (z-30 governs
              stacking, not DOM order). Replaces the old inline
              VoiceOrbMessage. [FB34] Mounted for the WHOLE call-phase
              lifecycle (`callPhase !== 'idle'`) — CallScreen itself renders
              the connecting/failed treatments; gating on `isCalling` here
              would have delayed the mount until well after the tap, which
              is exactly the "shows nothing for 2–5s" bug this fixes. */}
          <AnimatePresence mode="wait">
            {callPhase !== 'idle' && <CallScreen />}
          </AnimatePresence>
        </div>
      </AssistantLayout>
    </>
  );
}

// ── Page export ───────────────────────────────────────────────────

export default function AIAssistantV2Page() {
  return (
    <Suspense>
      <ChatProvider>
        <AssistantV2Content />
        {/* Sonner mount — used by useAssistantConnection.ts to surface
            FATAL-bucket errors as a second channel when the inline
            system message render path could plausibly be missed. */}
        <Toaster position="top-center" richColors closeButton />
      </ChatProvider>
    </Suspense>
  );
}
